/**
 * Service Intro Service
 * Fetches service introduction information from backend API
 */

// 1.3.45: 支持本地 Supabase 开发环境
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PROJECT_REF = import.meta.env.VITE_SUPABASE_PROJECT_REF;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// 1.3.45: 开发环境调试 - 检查环境变量加载
if (import.meta.env.DEV && !SUPABASE_ANON_KEY) {
  console.warn('⚠️ Service Intro Service: VITE_SUPABASE_ANON_KEY is not set!');
  console.warn('   Please check .env.local file and restart the dev server.');
}

// 优先使用 VITE_SUPABASE_URL（支持本地开发）
const SERVICE_INTRO_SERVICE_URL = SUPABASE_URL
  ? `${SUPABASE_URL}/functions/v1/service-intro-service`
  : SUPABASE_PROJECT_REF
  ? `https://${SUPABASE_PROJECT_REF}.supabase.co/functions/v1/service-intro-service`
  : (() => {
      console.warn('VITE_SUPABASE_URL or VITE_SUPABASE_PROJECT_REF is not set. Service intro service may not work correctly.');
      return '';
    })();

export interface ServiceIntroInfo {
  title: string;
  intro: string[];
}

/**
 * Get service introduction information from backend API
 * @param language Language code (zh, ja, en). If not provided, will use current i18n language
 * @returns Service introduction information or null if failed
 */
export async function getServiceIntro(language?: string): Promise<ServiceIntroInfo | null> {
  if (!SERVICE_INTRO_SERVICE_URL) {
    console.error('Service intro service URL is not configured');
    return null;
  }

  try {
    // Build URL with language parameter if provided
    const url = language
      ? `${SERVICE_INTRO_SERVICE_URL}?lang=${language}`
      : SERVICE_INTRO_SERVICE_URL;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // 1.3.45: 始终添加 Authorization header（Supabase Edge Functions 必需）
    // 如果 anon key 为空，仍然添加 header，让服务器返回明确的错误信息
    if (SUPABASE_ANON_KEY) {
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    } else {
      // 1.3.45: 开发环境警告
      if (import.meta.env.DEV) {
        console.error('❌ Service Intro Service: VITE_SUPABASE_ANON_KEY is empty!');
        console.error('   Request will fail with 401. Please check .env.local and restart dev server.');
      }
      // 仍然添加空的 Authorization header，让服务器返回明确的错误
      headers['Authorization'] = 'Bearer ';
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      // 1.3.45: 添加详细的错误信息
      if (response.status === 401) {
        console.error('❌ Service Intro Service 401 Unauthorized:', {
          status: response.status,
          statusText: response.statusText,
          url,
          hasAnonKey: !!SUPABASE_ANON_KEY,
          anonKeyLength: SUPABASE_ANON_KEY?.length || 0,
          anonKeyPrefix: SUPABASE_ANON_KEY?.substring(0, 20) || 'EMPTY',
        });
        if (!SUPABASE_ANON_KEY) {
          console.error('💡 解决方案: VITE_SUPABASE_ANON_KEY 未设置！请检查 .env.local 并重启开发服务器');
        }
      } else {
        console.error('Failed to fetch service intro info:', response.status, response.statusText);
      }
      return null;
    }

    const data = await response.json();
    return {
      title: data.title,
      intro: data.intro || [],
    };
  } catch (error) {
    console.error('Error fetching service intro info:', error);
    return null;
  }
}
